import React, { FC, useContext, useEffect, useState } from 'react';

import { CSSTransition } from 'react-transition-group';
import SwipeableViews from 'react-swipeable-views';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import { IconButton } from '@material-ui/core';

import { IGame } from '../../../../../../store/player/games/store';
import { GlobalContext } from '../../../../../../store';
import { IHoleScore, IScore } from '../../../../../../store/player/scores/store';

import { useRouteInfoState } from '../../../../../../components/RouteInfo/Context/RouteInfo.context';
import { sortBy } from '../../../../../../contrib/sortBy';
import { TabPanel } from '../../../../../../components/RoundScoreSummary/Components/RoundScoreSummaryMainTable/Components/TabPanel';
import { GroupScorecardsSummaryTable } from './Components/GroupScorecardsSummaryTable';
import Button from '../../../../../../components/Button';
import { GrupScorecardsStyled } from './GroupScorecards.styles';
import { zip18s } from './Utils/zip18s';
import { useGetPlayerEvent } from '../../../../../../contrib/Hooks/useGetPlayerEvent';
import { groupBy } from '../../../../../../contrib/utils';
import { zeros18 } from './Utils/zeros18';

export interface IGroupHoleResults {
  holeGrossResults: string[];
  playerName: string;
}

interface IProps {
  withdrawMode: boolean;
  toggleSimpleScorecardsIsVisible: () => void;
  game: IGame;
}

const GroupScorecards: FC<IProps> = props => {
  const { withdrawMode, toggleSimpleScorecardsIsVisible, game } = props;

  const {
    actions: {
      holeResults: { get: getHoleResults },
      roundScores: { get: getRoundScores }
    },
    scores
  } = useContext(GlobalContext);

  const { roundId, eventId, clubId } = useRouteInfoState();

  const event = useGetPlayerEvent(eventId);

  const [groupHoleResults, setGroupHoleResults] = useState<IGroupHoleResults[]>([]);
  const [enrollmentsIdsToNameMap, setEnrollmentsIdsToNameMap] = useState<Record<number, string>>(
    {}
  );
  const [pars, setPars] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [groupScores, setGroupScores] = useState<IScore[]>([]);

  const handleChangeIndex = (index: number) => {
    setPage(index);
  };

  const togglePage = () => {
    setPage(page === 0 ? 1 : 0);
  };

  useEffect(() => {
    if (game) {
      const groupId = game.group.id;

      if (scores[groupId]) {
        setGroupScores(scores[groupId]);
      }
    }
  }, [game, scores]);

  useEffect(() => {
    if (groupScores.length > 0) {
      const groupScore = groupScores[0];
      const sortedScores: IHoleScore[] = sortBy(groupScore.scores, 'asc', 'order');
      const newPars = sortedScores.map(score => String(score.holePar));

      setPars(zip18s(newPars));
    }
  }, [groupScores]);

  useEffect(() => {
    if (game && roundId && clubId) {
      getRoundScores(Number(clubId), game.round.event, roundId);
    }
  }, [game, roundId, clubId]);

  useEffect(() => {
    if (roundId) {
      getHoleResults(roundId);
    }
  }, [roundId]);

  useEffect(() => {
    if (event) {
      const newGroupEnrollments = event.enrollments.reduce(
        (acc: Record<number, string>, enrollment) => ({
          ...acc,
          [enrollment.id]: `${enrollment.user.firstName} ${enrollment.user.lastName}`
        }),
        {}
      );

      setEnrollmentsIdsToNameMap(newGroupEnrollments);
    }
  }, [event]);

  useEffect(() => {
    const newGroupHoleResults = groupScores.map(score => {
      const scoresByHole: Record<string, IHoleScore[]> = groupBy(score.scores, 'order');
      const playerName = enrollmentsIdsToNameMap[score.enrollment] || '';

      const holeGrossResults = zeros18.map((emptyInditor: string, index: number) => {
        const holeScore = scoresByHole[index];

        if (holeScore) {
          return String(holeScore[0].gross);
        }

        return emptyInditor;
      });

      return {
        holeGrossResults,
        playerName
      };
    });

    setGroupHoleResults(newGroupHoleResults);
  }, [groupScores, enrollmentsIdsToNameMap]);

  return (
    <CSSTransition in={withdrawMode} timeout={500} classNames="fade" unmountOnExit={true}>
      <GrupScorecardsStyled>
        <div className="title">
          <h2>VIEW SCORECARD</h2>

          <IconButton className="close" onClick={toggleSimpleScorecardsIsVisible}>
            <CloseRoundedIcon color="primary" />
          </IconButton>
        </div>

        <SwipeableViews
          index={page}
          dir={!page ? 'x' : 'x-reverse'}
          onChangeIndex={handleChangeIndex}
        >
          <TabPanel value={page} index={0} dir={'x'}>
            <GroupScorecardsSummaryTable
              start={0}
              end={9}
              pars={pars}
              groupHoleResults={groupHoleResults}
            />
          </TabPanel>

          <TabPanel value={page} index={1} dir={'x'}>
            <GroupScorecardsSummaryTable
              start={9}
              end={18}
              pars={pars}
              groupHoleResults={groupHoleResults}
            />
          </TabPanel>
        </SwipeableViews>

        <Button onClick={togglePage} color="primary" fullWidth={false}>
          {page === 1 ? 'FRONT' : 'BACK'} 9
        </Button>
      </GrupScorecardsStyled>
    </CSSTransition>
  );
};

export default GroupScorecards;
